import {
  BUILDING_STAGES, BuildingType, SimState, StageCost, Tile,
} from './types';
import { PlotSystem } from './PlotSystem';
import { EventBus } from './events';

/**
 * Multi-stage construction. Player pays the first stage on placement; each
 * subsequent stage is paid by clicking the construction site and choosing
 * "Build next stage". Buildings under construction are not operational.
 */
export const ConstructionSystem = {
  /** True if the tile is part of a building that hasn't been fully built. */
  isUnderConstruction(tile: Tile): boolean {
    return !!tile.bld && !!tile.construction && tile.construction.stage < tile.construction.totalStages;
  },

  /** True if the building on this tile is operational (built, or fixture). */
  isOperational(tile: Tile): boolean {
    if (!tile.bld) return false;
    if (!tile.construction) return true;
    return tile.construction.stage >= tile.construction.totalStages;
  },

  /** Cost of the NEXT stage to build (or null if already done / wrong tile). */
  nextStageCost(state: SimState, c: number, r: number): StageCost | null {
    const anchor = PlotSystem.buildingAnchor(state, c, r);
    if (!anchor) return null;
    const t = anchor.tile;
    if (!t.bld || !t.construction) return null;
    const stages = BUILDING_STAGES[t.bld];
    const idx = t.construction.stage;            // already-paid count → next index
    return stages[idx] ?? null;
  },

  /** How much of `kind` is still needed for the next stage after
   *  subtracting what's already been deposited into stagePaid. */
  remainingNeed(tile: Tile, kind: 'money' | 'wood' | 'wheat' | 'stone'): number {
    if (!tile.bld || !tile.construction) return 0;
    const stages = BUILDING_STAGES[tile.bld];
    const cost = stages[tile.construction.stage];
    if (!cost) return 0;
    const need = cost[kind] ?? 0;
    const paid = tile.construction.stagePaid?.[kind] ?? 0;
    return Math.max(0, need - paid);
  },

  canAdvance(state: SimState, c: number, r: number): boolean {
    const cost = this.nextStageCost(state, c, r);
    if (!cost) return false;
    const anchor = PlotSystem.buildingAnchor(state, c, r);
    if (!anchor) return false;
    const paid = anchor.tile.construction?.stagePaid;
    // Inventory + crate must cover each requirement. Deposit method
    // below sweeps both into the crate when called.
    return (
      (cost.money ?? 0) <= state.res.money + (paid?.money ?? 0) &&
      (cost.wood  ?? 0) <= state.res.wood  + (paid?.wood  ?? 0) &&
      (cost.wheat ?? 0) <= state.res.basket + (paid?.wheat ?? 0) &&
      (cost.stone ?? 0) <= (state.res.stone ?? 0) + (paid?.stone ?? 0)
    );
  },

  /** Deposit as much of each material as the player has toward the
   *  next stage's cost — partial payment is fine. Emits one
   *  materialsDeposited event per kind so FlyToHud animates, then auto-
   *  advances the stage when stagePaid covers the full cost. */
  depositForNextStage(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const anchor = PlotSystem.buildingAnchor(state, c, r);
    if (!anchor) return false;
    const t = anchor.tile;
    if (!t.bld || !t.construction) {
      bus.emit({ type: 'blocked', reason: 'Already built.' });
      return false;
    }
    if (!t.construction.stagePaid) {
      t.construction.stagePaid = { money: 0, wood: 0, wheat: 0, stone: 0 };
    }
    const paid = t.construction.stagePaid;
    const stages = BUILDING_STAGES[t.bld];
    const cost = stages[t.construction.stage];
    if (!cost) return false;
    const moves: { kind: 'money' | 'wood' | 'wheat' | 'stone'; qty: number }[] = [];
    const tryMove = (
      kind: 'money' | 'wood' | 'wheat' | 'stone',
      readInv: () => number,
      writeInvDelta: (delta: number) => void,
    ) => {
      const need = (cost[kind] ?? 0) - paid[kind];
      if (need <= 0) return;
      const have = readInv();
      const move = Math.min(need, have);
      if (move <= 0) return;
      writeInvDelta(-move);
      paid[kind] += move;
      moves.push({ kind, qty: move });
    };
    tryMove('money', () => state.res.money,         d => { state.res.money += d; });
    tryMove('wood',  () => state.res.wood,          d => { state.res.wood  += d; });
    tryMove('wheat', () => state.res.basket,        d => { state.res.basket += d; });
    tryMove('stone', () => state.res.stone ?? 0,    d => { state.res.stone = (state.res.stone ?? 0) + d; });
    if (moves.length === 0) {
      bus.emit({ type: 'hint', text: 'Nothing to deposit — gather the missing materials.' });
      return false;
    }
    for (const m of moves) {
      bus.emit({ type: 'materialsDeposited', col: anchor.c, row: anchor.r, kind: m.kind, qty: m.qty });
    }
    const fully = (k: 'money' | 'wood' | 'wheat' | 'stone') => (cost[k] ?? 0) <= paid[k];
    if (fully('money') && fully('wood') && fully('wheat') && fully('stone')) {
      // Burn the deposited materials and bump the stage counter.
      t.construction.stage++;
      t.construction.stagePaid = { money: 0, wood: 0, wheat: 0, stone: 0 };
      const bld = t.bld as BuildingType;
      bus.emit({
        type: 'constructionAdvanced',
        col: anchor.c, row: anchor.r,
        bld,
        stage: t.construction.stage,
        total: t.construction.totalStages,
      });
      if (t.construction.stage >= t.construction.totalStages) {
        delete t.construction;
        bus.emit({ type: 'constructionComplete', col: anchor.c, row: anchor.r, bld });
        bus.emit({ type: 'hint', text: `${bld} construction complete — it's now operational!` });
      }
    }
    return true;
  },

  /** Legacy "build next stage" — kept for any callers that want the
   *  all-or-nothing behavior. Now wraps depositForNextStage: when the
   *  player has enough for the full stage cost the deposit fills the
   *  crate in one shot and triggers the advance immediately. */
  advance(state: SimState, c: number, r: number, bus: EventBus): boolean {
    if (!this.canAdvance(state, c, r)) {
      const cost = this.nextStageCost(state, c, r);
      if (cost) {
        const anchor = PlotSystem.buildingAnchor(state, c, r);
        const paid = anchor?.tile.construction?.stagePaid;
        const short = (['money', 'wood', 'wheat', 'stone'] as const).map(k => {
          const have = k === 'money' ? state.res.money
            : k === 'wood' ? state.res.wood
            : k === 'wheat' ? state.res.basket
            : (state.res.stone ?? 0);
          const need = (cost[k] ?? 0) - (paid?.[k] ?? 0);
          const gap = need - have;
          return gap > 0 ? `${gap} ${k}` : '';
        }).filter(Boolean).join(', ');
        bus.emit({ type: 'blocked', reason: `Missing: ${short}` });
      }
      return false;
    }
    return this.depositForNextStage(state, c, r, bus);
  },
};
